import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { reconcileClassification } from './classificationReconcile';
import DiscoveryReviewPanel from './DiscoveryReviewPanel';
import CategoryEditPanel, { buildResponseRows, HEADER_TIPS } from './CategoryEditPanel';
import ResponseDrillTable from './ResponseDrillTable';
import { labelToIndex, OTHER_LABEL } from './categoryLabels';
import {
    fetchClassification, listLabels, startClassify, getClassifyJob,
    startDiscover, fetchDiscovery, putDesignCategories,
} from '../../utils/explorerApi';
import { exportRowsCsv, exportChartAndTablePdf, buildFilename } from '../../utils/exportUtils';

// Palette cloned from SetMemberCanvas so the classified-text surface reads as
// part of the same analysis family.
const COLORS = ['#5B9FFF', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const OTHER_COLOR = '#9AA0A6';

const POLL_MS = 1500;
const tokensPrimaryDeep = '#1558B0';

// A finished run reports success whether or not it had anything to do, so the
// author is told what it covered. Exported for direct testing.
export function classifyOutcomeMessage(status) {
    const eligible = Number(status?.eligible_count);
    const classified = Number(status?.classified_count);
    if (!Number.isFinite(eligible) || !Number.isFinite(classified)) return 'Categorizing finished.';
    if (eligible === 0) return 'No answers were available to categorize.';
    return `Categorized ${classified} of ${eligible} answer${eligible === 1 ? '' : 's'}.`;
}

// What a classifier knows about an answer beyond who said it and what they
// said. No other response type has these, so no other canvas asks for them.
const DRILL_COLUMNS = [
    { header: 'Score', title: HEADER_TIPS.score },
    { header: 'Margin', title: HEADER_TIPS.margin },
    { header: 'Placement', title: HEADER_TIPS.placement },
];

// The bar and its drill-down must agree about which answers are in a bucket,
// so both resolve an author's label through the same function.
export const resolveDrillLabel = (categories, label) => labelToIndex(categories, label);

// How an answer came to sit in the bucket the author just clicked.
//
// The drill-down used to print the classifier's stored `decision` and
// `decision_path`. Those record what the LAST CLASSIFY RUN concluded and are
// never rewritten when an author assigns an answer, so a response the author
// had placed by hand still read "ambiguous — pending author review" while
// sitting in the category they had placed it in. The row was settled; the
// column said it was waiting.
//
// The words here are the ones already on the chart legend, so the table and
// the bars describe the same thing the same way.
export function placementOf(row, label, drillIndex) {
  const judged = label !== undefined && label !== null && label !== '';
  if (judged) return 'Assigned by you';
  if (drillIndex === 'other') return 'Did not fit a category';
  if (row?.decision === 'assigned') return 'High-confidence match';
  if (row?.decision === 'ambiguous') return 'Possible match';
  if (row?.decision === 'other') return 'Did not fit a category';
  return 'Not categorized';
}

// Build a session_id → label Map from the raw labels rows the API returns.
function labelsToMap(labelRows) {
    const m = new Map();
    (Array.isArray(labelRows) ? labelRows : []).forEach(r => {
        if (r && r.session_id != null) m.set(r.session_id, r.label);
    });
    return m;
}

export default function ClassifiedTextCanvas({ question, deploymentId, responses, refreshKey = 0, onStructureRefresh, staleHint = false, onStaleHintClear, saveSlot, onOpenSession, view = 'vertical-bars', onChartReadyChange }) {
    const qid = question?.id;
    const categories = useMemo(() => question?.responseCategories || [], [question]);

    const [rows, setRows] = useState([]);
    const [labelsBySession, setLabelsBySession] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Classify job state.
    const [job, setJob] = useState(null); // { status, progress } | null
    const [jobError, setJobError] = useState(null);
    // What the last completed run covered, shown so success is legible.
    const [jobOutcome, setJobOutcome] = useState(null);

    // WP-D3b: discovery state (dendrogram fetch + job + adoption).
    const [discovery, setDiscovery] = useState(null);
    const [discoveryJob, setDiscoveryJob] = useState(null);
    const [discoveryError, setDiscoveryError] = useState(null);
    const [adoptBusy, setAdoptBusy] = useState(false);
    const [adoptError, setAdoptError] = useState(null);

    // Display-only controls.
    const [showConfidence, setShowConfidence] = useState(true);
    const [drillIndex, setDrillIndex] = useState(null); // number | 'other' | null

    // Category edit overlay (rename + per-response reassignment) and export menu.
    const [catEditOpen, setCatEditOpen] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const chartRef = useRef(null);

    const mountedRef = useRef(true);
    const pollTimerRef = useRef(null);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    }, []);

    // Load classification rows + labels. Called on mount, on qid/deployment
    // change, and whenever the parent bumps refreshKey (after a judgment lands
    // in the Ambiguous review panel) so the chart reflects author decisions.
    const loadData = useCallback(async () => {
        if (!deploymentId || !qid) { setLoading(false); return; }
        setLoading(true);
        setError(null);
        try {
            const [classRows, labelRows] = await Promise.all([
                fetchClassification(deploymentId, qid),
                listLabels(deploymentId, qid),
            ]);
            if (!mountedRef.current) return;
            setRows(Array.isArray(classRows) ? classRows : []);
            setLabelsBySession(labelsToMap(labelRows));
        } catch (e) {
            if (!mountedRef.current) return;
            setError(e?.message || 'Failed to load classification');
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [deploymentId, qid]);

    useEffect(() => { loadData(); }, [loadData, refreshKey]);

    // Tell the panel head whether a chart is on screen, so the bar-orientation
    // control is offered only when there are bars to orient. Loading, an error,
    // and the not-yet-categorized states all draw prose instead of a chart.
    const chartShowing = !loading && !error && rows.length > 0;
    useEffect(() => {
        if (!onChartReadyChange) return undefined;
        onChartReadyChange(chartShowing);
        return () => onChartReadyChange(false);
    }, [chartShowing, onChartReadyChange]);

    // Refetch just the labels when the tab/window regains focus, so judgments
    // made elsewhere show up without a full reload.
    useEffect(() => {
        const onFocus = async () => {
            if (!deploymentId || !qid) return;
            try {
                const labelRows = await listLabels(deploymentId, qid);
                if (mountedRef.current) setLabelsBySession(labelsToMap(labelRows));
            } catch (_) { /* non-fatal */ }
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [deploymentId, qid]);

    // ---- Classify job polling ----
    const pollJob = useCallback((jobId) => {
        const tick = async () => {
            try {
                const status = await getClassifyJob(deploymentId, qid, jobId);
                if (!mountedRef.current) return;
                setJob(status);
                const s = String(status?.status || '').toLowerCase();
                if (s === 'succeeded' || s === 'success' || s === 'completed') {
                    setJob(null);
                    setJobOutcome(classifyOutcomeMessage(status));
                    await loadData();
                    return;
                }
                if (s === 'failed' || s === 'error') {
                    setJobError(status?.error || 'Classification failed');
                    setJob(null);
                    return;
                }
                pollTimerRef.current = setTimeout(tick, POLL_MS);
            } catch (e) {
                if (!mountedRef.current) return;
                setJobError(e?.message || 'Classification job polling failed');
                setJob(null);
            }
        };
        pollTimerRef.current = setTimeout(tick, POLL_MS);
    }, [deploymentId, qid, loadData]);

    const runClassify = useCallback(async () => {
        setJobError(null);
        setJobOutcome(null);
        onStaleHintClear?.();
        setJob({ status: 'starting', progress: 0 });
        try {
            const jobId = await startClassify(deploymentId, qid);
            if (!mountedRef.current) return;
            if (!jobId) {
                // Some backends classify synchronously — just reload.
                setJob(null);
                await loadData();
                return;
            }
            pollJob(jobId);
        } catch (e) {
            if (!mountedRef.current) return;
            setJobError(e?.message || 'Failed to start classification');
            setJob(null);
        }
    }, [deploymentId, qid, pollJob, loadData, onStaleHintClear]);

    // ---- WP-D3b: category discovery ----

    // A prior discovery result (if any) loads with the canvas so the author
    // returns straight to the cluster review, not a blank Discover button.
    useEffect(() => {
        let cancelled = false;
        setDiscovery(null);
        setDiscoveryError(null);
        if (!deploymentId || !qid) return undefined;
        fetchDiscovery(deploymentId, qid)
            .then(row => { if (!cancelled && mountedRef.current) setDiscovery(row); })
            .catch(() => { /* never-discovered/404 is the normal cold start */ });
        return () => { cancelled = true; };
    }, [deploymentId, qid]);

    const pollDiscoverJob = useCallback((jobId) => {
        const tick = async () => {
            try {
                const status = await getClassifyJob(deploymentId, qid, jobId);
                if (!mountedRef.current) return;
                setDiscoveryJob(status);
                const s = String(status?.status || '').toLowerCase();
                if (s === 'succeeded' || s === 'success' || s === 'completed') {
                    setDiscoveryJob(null);
                    const row = await fetchDiscovery(deploymentId, qid);
                    if (mountedRef.current) setDiscovery(row);
                    return;
                }
                if (s === 'failed' || s === 'error') {
                    setDiscoveryError(status?.error || 'Discovery failed');
                    setDiscoveryJob(null);
                    return;
                }
                pollTimerRef.current = setTimeout(tick, POLL_MS);
            } catch (e) {
                if (!mountedRef.current) return;
                setDiscoveryError(e?.message || 'Discovery job polling failed');
                setDiscoveryJob(null);
            }
        };
        pollTimerRef.current = setTimeout(tick, POLL_MS);
    }, [deploymentId, qid]);

    const runDiscover = useCallback(async (scope = '') => {
        setDiscoveryError(null);
        setAdoptError(null);
        setDiscoveryJob({ status: 'starting', progress: 0 });
        try {
            const jobId = await startDiscover(deploymentId, qid, scope);
            if (!mountedRef.current) return;
            if (!jobId) {
                setDiscoveryJob(null);
                const row = await fetchDiscovery(deploymentId, qid);
                if (mountedRef.current) setDiscovery(row);
                return;
            }
            pollDiscoverJob(jobId);
        } catch (e) {
            if (!mountedRef.current) return;
            setDiscoveryError(e?.message || 'Failed to start discovery');
            setDiscoveryJob(null);
        }
    }, [deploymentId, qid, pollDiscoverJob]);

    // Adopt named clusters as the question's response categories (initial
    // adoption — the categories list is empty here). The descriptor write
    // feeds both the classifier and the structure endpoint; refreshing the
    // structure flips this canvas to the "Not categorized yet" state with an
    // active Categorize button (classification stays author-triggered).
    const adoptCategories = useCallback(async (cats) => {
        setAdoptBusy(true);
        setAdoptError(null);
        try {
            await putDesignCategories(deploymentId, qid, cats);
            if (!mountedRef.current) return;
            onStructureRefresh?.();
        } catch (e) {
            if (!mountedRef.current) return;
            setAdoptError(e?.message || 'Failed to adopt categories');
        } finally {
            if (mountedRef.current) setAdoptBusy(false);
        }
    }, [deploymentId, qid, onStructureRefresh]);

    // Reload just the author labels after a manual reassignment in the edit
    // panel (labels are the override overlay; the classification rows are
    // untouched, so the chart re-runs the reconciler with the new overrides).
    const refreshLabels = useCallback(async () => {
        try {
            const labelRows = await listLabels(deploymentId, qid);
            if (mountedRef.current) setLabelsBySession(labelsToMap(labelRows));
        } catch (_) { /* non-fatal */ }
    }, [deploymentId, qid]);

    // ---- Reconcile + chart data ----
    const recon = useMemo(
        () => reconcileClassification(rows, categories, labelsBySession),
        [rows, categories, labelsBySession]
    );

    const textBySession = useMemo(
        () => new Map((responses || []).map(r => [r.session_id, r[question?.name]])),
        [responses, question]
    );

    // How a recipient is NAMED for the author. Explore's session list and the
    // SRA results header both show the email, so printing the recipient_id UUID
    // here left an author with an identifier no other screen displays — they
    // had to text-search Explore to find the person they had just clicked.
    const recipientIdBySession = useMemo(
        () => new Map((responses || []).map(r => [
            r.session_id,
            r.recipient_email || r.recipient_name || r.recipient_id || null,
        ])),
        [responses]
    );
    const audioBySession = useMemo(
        () => new Map((responses || []).map(r => [r.session_id, r?.audio_by_variable?.[question?.name] || ''])),
        [responses, question]
    );
    const loadedResponseCount = Array.isArray(responses) ? responses.length : null;

    // The chart's n is the number of CATEGORIZED answers, which is not the
    // number of answers: categorizing runs on demand, so answers recorded since
    // the last run have no classification row yet.  Count the real answers from
    // the responses themselves so the two are never conflated on screen.
    const answeredCount = useMemo(() => {
        if (!Array.isArray(responses) || !question?.name) return null;
        return responses.reduce((total, response) => {
            const value = response?.[question.name];
            return total + (value !== null && value !== undefined && String(value).trim() !== '' ? 1 : 0);
        }, 0);
    }, [responses, question]);
    const uncategorizedCount = answeredCount == null ? 0 : Math.max(0, answeredCount - recon.n);

    // Response-level export rows (same normalization the edit table shows).
    const exportRowsData = useMemo(
        () => buildResponseRows({ rows, labelsBySession, textBySession, recipientIdBySession, audioBySession, categories }),
        [rows, labelsBySession, textBySession, recipientIdBySession, audioBySession, categories]
    );
    const exportColumns = useMemo(() => ([
        { header: 'Recipient ID', get: (r) => r.recipientId },
        { header: 'Response', get: (r) => r.text },
        { header: 'Category', get: (r) => r.catName },
        { header: 'Status', get: (r) => r.status },
        { header: 'Decision', get: (r) => r.decision },
        { header: 'Score', get: (r) => (r.score != null ? r.score.toFixed(4) : '') },
        { header: 'Margin', get: (r) => (r.margin != null ? r.margin.toFixed(4) : '') },
        { header: 'Path', get: (r) => r.path },
        { header: 'Session', get: (r) => r.sid },
    ]), []);
    const doExportCsv = useCallback(() => {
        const fname = buildFilename(question?.label || question?.name || 'question', deploymentId, 'categories', 'csv');
        exportRowsCsv(exportRowsData, exportColumns, fname);
    }, [exportRowsData, exportColumns, question, deploymentId]);
    const doExportPdf = useCallback(() => {
        exportChartAndTablePdf({
            containerRef: chartRef,
            title: (question?.label || question?.name || 'Question') + ' — categories',
            columns: exportColumns,
            rows: exportRowsData,
        });
    }, [exportRowsData, exportColumns, question]);

    const chartData = useMemo(() => {
        const cats = recon.perCategory
            .map(c => ({ label: c.summary, solid: c.solid, faded: c.faded, key: c.index }))
            .sort((a, b) => (b.solid + b.faded) - (a.solid + a.faded));
        // Cumulative % is over the CATEGORY bars only (Other is excluded from
        // the denominator — it isn't part of the semantic Pareto).
        const denom = cats.reduce((s, c) => s + c.solid + c.faded, 0);
        let running = 0;
        cats.forEach(c => {
            running += c.solid + c.faded;
            c.cumPct = denom > 0 ? (running / denom) * 100 : 0;
        });
        // Other pinned last; not part of cumulative %.
        cats.push({ label: 'Did not fit a category', solid: recon.other, faded: 0, key: 'other', cumPct: null, isOther: true });
        return cats;
    }, [recon]);

    const fmt = (v) => (typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v);
    // A classify job is running. Both the not-yet-classified state and the
    // uncategorized-remainder prompt below disable their action while it runs.
    const busy = !!job;

    // ---- Empty / not-classified states ----
    if (loading) {
        return <div style={panelMsgStyle}>Loading classification…</div>;
    }
    if (error) {
        return <div style={{ ...panelMsgStyle, color: '#C62828' }}>{error}</div>;
    }

    const isClassified = rows.length > 0;
    const hasCategories = Array.isArray(categories) && categories.length > 0;

    if (!isClassified) {
        const discoverBusy = !!discoveryJob;
        return (
            <div style={{ fontFamily: 'var(--font-family-primary)', padding: 24, textAlign: 'center', color: '#5F6368' }}>
                {!hasCategories ? (
                    <>
                        <div style={{ fontSize: 15, color: '#202124', marginBottom: 8 }}>No categories authored</div>
                        <div style={{ fontSize: 13, marginBottom: 16 }}>
                            Author categories in the Designer — or discover candidates from the answers themselves
                            (deterministic similarity grouping, no AI authorship; you name what gets adopted).
                        </div>
                        {discoveryError && <div style={{ color: '#C62828', fontSize: 13, marginBottom: 12 }}>{discoveryError}</div>}
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: discovery ? 24 : 0 }}>
                            <button onClick={runDiscover} disabled={discoverBusy || adoptBusy} style={{ ...ctaStyle, ...(discoverBusy ? { opacity: 0.7, cursor: 'wait' } : {}) }}>
                                {discoverBusy
                                    ? `Discovering… ${discoveryJob?.progress != null ? Math.round(Number(discoveryJob.progress) * (Number(discoveryJob.progress) <= 1 ? 100 : 1)) + '%' : ''}`
                                    : (discovery ? 'Re-discover' : 'Discover categories')}
                            </button>
                            <button disabled title="Author or adopt categories first" style={{ ...ctaStyle, color: '#9AA0A6', backgroundColor: '#F8F9FA', border: '1px solid #DADCE0', cursor: 'not-allowed' }}>Categorize</button>
                        </div>
                        {discovery && !discoverBusy && (
                            <DiscoveryReviewPanel
                                discovery={discovery}
                                textBySession={textBySession}
                                onAdopt={adoptCategories}
                                busy={adoptBusy}
                                error={adoptError}
                                saveSlot={saveSlot}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <div style={{ fontSize: 15, color: '#202124', marginBottom: 8 }}>Not categorized yet</div>
                        <div style={{ fontSize: 13, marginBottom: 16 }}>Group these free-text answers into the authored categories to see the Semantic Volume Pareto.</div>
                        {jobError && <div style={{ color: '#C62828', fontSize: 13, marginBottom: 12 }}>{jobError}</div>}
                        <button onClick={runClassify} disabled={busy} style={{ ...ctaStyle, ...(busy ? { opacity: 0.7, cursor: 'wait' } : {}) }}>
                            {busy ? `Categorizing… ${job?.progress != null ? Math.round(Number(job.progress) * (Number(job.progress) <= 1 ? 100 : 1)) + '%' : ''}` : 'Categorize'}
                        </button>
                    </>
                )}
            </div>
        );
    }

    // ---- Classified state ----
    const solidTotal = recon.perCategory.reduce((s, c) => s + c.solid, 0);
    const fadedTotal = recon.perCategory.reduce((s, c) => s + c.faded, 0);

    // Members of the drilled bucket. Mirrors the reducer's assignment rules so
    // the table reconciles with the bar it came from.
    const drillRows = drillIndex == null ? [] : rows.filter(r => {
        if (!r || typeof r !== 'object') return false;
        // An author assignment names its category by id. This used to read the
        // label with Number(), which is NaN for an id, so every answer the
        // author had placed was filtered OUT of the bucket it was placed in —
        // a bar reading 12 opened a table of 1. Resolve it the same way the
        // reducer behind the bar resolves it, or the two disagree by
        // construction.
        const label = resolveDrillLabel(categories, labelsBySession.get(r.session_id));
        if (drillIndex === 'other') {
            if (label !== null) return label === OTHER_LABEL;
            return r.decision === 'other';
        }
        if (label !== null) {
            return label !== OTHER_LABEL && label === drillIndex;
        }
        if (r.decision === 'assigned') return Number(r.assigned_category_index) === drillIndex;
        if (r.decision === 'ambiguous' && r.fractional_mass && typeof r.fractional_mass === 'object') {
            return Object.prototype.hasOwnProperty.call(r.fractional_mass, String(drillIndex));
        }
        return false;
    });

    // Bar orientation. Vertical is the Pareto's classic reading (tallest bar
    // first, left to right); horizontal trades that for room to read the
    // category names, which here are whole sentences.
    const isHorizontal = view === 'horizontal-bars';
    const axisTick = { fill: '#5F6368', fontSize: 12 };
    const axisStroke = { stroke: '#E8EAED' };
    // Even a full line has an end. Truncate rather than let a long name run
    // under the plot area.
    const truncateCategory = (v) => (typeof v === 'string' && v.length > 34 ? `${v.slice(0, 33)}…` : v);

    const solidCells = chartData.map((entry, i) => (
        <Cell key={`solid-${i}`} fill={entry.isOther ? OTHER_COLOR : COLORS[i % COLORS.length]} />
    ));
    const fadedCells = chartData.map((entry, i) => (
        <Cell key={`faded-${i}`} fill={entry.isOther ? OTHER_COLOR : COLORS[i % COLORS.length]} fillOpacity={0.4} />
    ));
    const onBarClick = (d) => setDrillIndex(d?.key ?? null);
    const tooltip = (
        <Tooltip
            contentStyle={{ fontFamily: 'var(--font-family-primary)' }}
            formatter={(value, name) => [fmt(value), name]}
        />
    );

    const drillLabel = drillIndex === 'other'
        ? 'Did not fit a category'
        : (recon.perCategory.find(c => c.index === drillIndex)?.summary || `Category ${drillIndex}`);

    return (
        <div style={{ fontFamily: 'var(--font-family-primary)', width: '100%' }}>
            {/* Header row: n, pending-review chip, controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                {/* `N` is the survey-level ratio in the Explorer header, so `n`
                    stays the count of answers to this question. Categorized is a
                    separate quantity the author controls and may never equal
                    `n`, so it keeps its own reserved letter, `c`. */}
                {answeredCount != null && (
                    <span style={{ fontSize: 13, color: '#202124', fontWeight: 600 }} title="Answers to this question">
                        n = {fmt(answeredCount)}
                    </span>
                )}
                <span style={{ fontSize: 13, color: '#202124', fontWeight: 600 }} title="Categorized answers">
                    c = {fmt(recon.n)}
                </span>
                {/* Answers that fit no existing category are the only ones a new
                    category could describe, so the chain is offered on exactly
                    them: categorize, then look for categories in what is left. */}
                {recon.other > 0 && !discovery && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        backgroundColor: '#F1F3F4', color: '#3C4043',
                        border: '1px solid #DADCE0', borderRadius: 12,
                        padding: '3px 12px', fontSize: 13, fontWeight: 600
                    }}>
                        {fmt(recon.other)} fit no category
                        <button
                            onClick={() => runDiscover('residue')}
                            disabled={!!discoveryJob}
                            title="Look for new categories in only the answers that fit none of the existing ones"
                            style={{ background: 'none', border: 'none', padding: 0, color: tokensPrimaryDeep, font: 'inherit', textDecoration: 'underline', cursor: discoveryJob ? 'wait' : 'pointer' }}
                        >
                            {discoveryJob ? 'Looking…' : 'Suggest categories for them'}
                        </button>
                    </span>
                )}
                {uncategorizedCount > 0 && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        backgroundColor: '#FFF4E5', color: '#9A5B00',
                        border: '1px solid #F0C88A', borderRadius: 12,
                        padding: '3px 12px', fontSize: 13, fontWeight: 600
                    }}>
                        {fmt(uncategorizedCount)} of {fmt(answeredCount)} answers not yet categorized
                        <button
                            onClick={runClassify}
                            disabled={busy}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#9A5B00', font: 'inherit', textDecoration: 'underline', cursor: busy ? 'wait' : 'pointer' }}
                        >
                            {busy ? 'Categorizing…' : 'Categorize them'}
                        </button>
                    </span>
                )}
                {recon.ambiguous > 0 && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        backgroundColor: '#FFF4E5', color: '#9A5B00',
                        border: '1px solid #F0C88A', borderRadius: 12,
                        padding: '3px 12px', fontSize: 13, fontWeight: 600
                    }}>
                        ⚠ {recon.ambiguous} answer{recon.ambiguous === 1 ? '' : 's'} awaiting review
                    </span>
                )}
                <div style={{ flex: 1 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5F6368', cursor: 'pointer' }}>
                    <input type="checkbox" checked={showConfidence} onChange={(e) => setShowConfidence(e.target.checked)} style={{ cursor: 'pointer' }} />
                    Show possible matches
                </label>
                {staleHint && (
                    <span style={{
                        fontSize: 12, color: '#9A5B00', backgroundColor: '#FFF4E5',
                        border: '1px solid #F0C88A', borderRadius: 12, padding: '3px 10px'
                    }}>
                        categories changed — re-run classification to apply
                    </span>
                )}
                <div style={{ position: 'relative' }}>
                    <button onClick={() => setExportMenuOpen(o => !o)} style={topBtnStyle}>Export ▾</button>
                    {exportMenuOpen && (
                        <>
                            <div onClick={() => setExportMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 50, background: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 210, padding: '4px 0' }}>
                                <button style={exportMenuItemStyle} onClick={() => { setExportMenuOpen(false); doExportCsv(); }}>Data table (CSV)</button>
                                <button style={exportMenuItemStyle} onClick={() => { setExportMenuOpen(false); doExportPdf(); }}>Chart + table (PDF)</button>
                            </div>
                        </>
                    )}
                </div>
                <button onClick={() => setCatEditOpen(true)} disabled={!!job} style={{
                    fontSize: 12, padding: '4px 12px', borderRadius: 4, fontWeight: 600,
                    border: '1px solid #1967D2', background: '#1967D2', color: '#FFFFFF',
                    cursor: job ? 'wait' : 'pointer'
                }}>
                    Edit categories
                </button>
            </div>

            {jobError && <div style={{ color: '#C62828', fontSize: 13, marginBottom: 8 }}>{jobError}</div>}

            {/* Semantic Volume Pareto (chartRef captures it for PDF export) */}
            <div ref={chartRef}>
            {/* Horizontal bars grow the plot with the category count so every
                bar keeps a readable row; vertical keeps one fixed height. */}
            <ResponsiveContainer width="100%" height={isHorizontal ? Math.max(300, chartData.length * 48 + 70) : 340}>
                {isHorizontal ? (
                    <ComposedChart layout="vertical" data={chartData} margin={{ top: 24, right: 30, left: 10, bottom: 10 }}>
                        <XAxis xAxisId="vol" type="number" allowDecimals={false} tick={axisTick} axisLine={axisStroke} tickLine={axisStroke} />
                        {/* The percentage scale moves to the top edge so it does
                            not collide with the count scale below the bars. */}
                        <XAxis xAxisId="pct" type="number" orientation="top" domain={[0, 100]} unit="%" tick={axisTick} axisLine={axisStroke} tickLine={axisStroke} />
                        <YAxis
                            type="category"
                            dataKey="label"
                            width={220}
                            interval={0}
                            tickFormatter={truncateCategory}
                            tick={axisTick}
                            axisLine={axisStroke}
                            tickLine={axisStroke}
                        />
                        {tooltip}
                        <Bar xAxisId="vol" dataKey="solid" stackId="v" name="High-confidence matches" onClick={onBarClick} cursor="pointer">
                            {solidCells}
                        </Bar>
                        {showConfidence && (
                            <Bar xAxisId="vol" dataKey="faded" stackId="v" name="Possible matches" onClick={onBarClick} cursor="pointer">
                                {fadedCells}
                            </Bar>
                        )}
                        <Line xAxisId="pct" dataKey="cumPct" name="Cumulative percentage" stroke="#2B6BBF" strokeWidth={2} dot connectNulls={false} />
                    </ComposedChart>
                ) : (
                    <ComposedChart data={chartData} margin={{ top: 20, right: 40, left: 10, bottom: 60 }}>
                        <XAxis
                            dataKey="label"
                            tick={axisTick}
                            axisLine={axisStroke}
                            tickLine={axisStroke}
                            interval={0}
                            angle={-25}
                            textAnchor="end"
                            height={70}
                        />
                        <YAxis yAxisId="vol" tick={axisTick} axisLine={axisStroke} tickLine={axisStroke} />
                        <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tick={axisTick} axisLine={axisStroke} tickLine={axisStroke} unit="%" />
                        {tooltip}
                        <Bar yAxisId="vol" dataKey="solid" stackId="v" name="High-confidence matches" onClick={onBarClick} cursor="pointer">
                            {solidCells}
                        </Bar>
                        {showConfidence && (
                            <Bar yAxisId="vol" dataKey="faded" stackId="v" name="Possible matches" onClick={onBarClick} cursor="pointer">
                                {fadedCells}
                            </Bar>
                        )}
                        <Line yAxisId="pct" dataKey="cumPct" name="Cumulative percentage" stroke="#2B6BBF" strokeWidth={2} dot connectNulls={false} />
                    </ComposedChart>
                )}
            </ResponsiveContainer>
            <div aria-label="Chart legend" role="list" style={legendStyle}>
                <span role="listitem" style={legendItemStyle}>
                    <span aria-hidden="true" style={{ ...legendSwatchStyle, backgroundColor: '#5B9FFF' }} />
                    High-confidence matches
                </span>
                <span role="listitem" style={legendItemStyle}>
                    <span aria-hidden="true" style={{ ...legendSwatchStyle, backgroundColor: 'rgba(91, 159, 255, 0.4)' }} />
                    Possible matches
                </span>
                <span role="listitem" style={legendItemStyle}>
                    <span aria-hidden="true" style={legendLineStyle} />
                    Cumulative percentage
                </span>
            </div>
            </div>

            {!showConfidence && (
                <div style={{ fontSize: 12, color: '#9A5B00', marginTop: 4 }}>
                    Possible matches are hidden.
                </div>
            )}

            <div style={{ fontSize: 12, color: '#5F6368', marginTop: 10, padding: '8px 12px', backgroundColor: '#FAFBFC', border: '1px solid #E8EAED', borderRadius: 6 }}>
                {answeredCount != null && (
                    <>Answers to this question (n): {fmt(answeredCount)}{loadedResponseCount == null ? null : <> of {fmt(loadedResponseCount)} survey responses</>}{' · '}</>
                )}
                Categorized (c): {fmt(recon.n)}
                {uncategorizedCount > 0 && (
                    <span style={{ color: '#9A5B00', fontWeight: 600 }}> · Not yet categorized: {fmt(uncategorizedCount)}</span>
                )}
                {' · '}High-confidence matches: {fmt(solidTotal)}
                {fadedTotal > 0 && <> · Possible matches: {fmt(fadedTotal)}</>}
                {recon.other > 0 && <> · Did not fit a category: {fmt(recon.other)}</>}
                {recon.unaccounted > 0 && (
                    <span style={{ color: '#C62828', fontWeight: 600 }}> · Could not classify: {fmt(recon.unaccounted)}</span>
                )}
            </div>

            <div style={{ fontSize: 11, color: '#9AA0A6', marginTop: 4 }}>
                The blue line shows the percentage of answers counted as you move {isHorizontal ? 'from top to bottom' : 'from left to right'}. Answers that did not fit a category are not included. Click a bar to see its answers.
            </div>

            {/* Drill-down table */}
            {drillIndex != null && (
                <ResponseDrillTable
                    label={drillLabel}
                    rows={drillRows.map((r, i) => {
                        const score = drillIndex !== 'other' && Array.isArray(r.similarity) && r.similarity[drillIndex] != null
                            ? r.similarity[drillIndex]
                            : r.top_score;
                        return {
                            key: r.session_id || i,
                            recipientId: recipientIdBySession.get(r.session_id),
                            sessionId: r.session_id,
                            response: textBySession.has(r.session_id) && textBySession.get(r.session_id) != null
                                ? String(textBySession.get(r.session_id))
                                : '(response text unavailable)',
                            cells: [
                                score != null ? fmt(Number(score)) : '—',
                                r.margin != null ? fmt(Number(r.margin)) : '—',
                                placementOf(r, labelsBySession.get(r.session_id), drillIndex),
                            ],
                        };
                    })}
                    columns={DRILL_COLUMNS}
                    onClose={() => setDrillIndex(null)}
                    onOpenSession={onOpenSession}
                    recipientTip={HEADER_TIPS.recipient}
                    responseTip={HEADER_TIPS.response}
                />
            )}

            {catEditOpen && (
                <CategoryEditPanel
                    deploymentId={deploymentId}
                    question={question}
                    rows={rows}
                    labelsBySession={labelsBySession}
                    textBySession={textBySession}
                    audioBySession={audioBySession}
                    categories={categories}
                    onClose={() => setCatEditOpen(false)}
                    onLabelsRefresh={refreshLabels}
                    onStructureRefresh={onStructureRefresh}
                    onClassificationRefresh={loadData}
                    onReclassify={runClassify}
                    reclassifyBusy={!!job}
                    reclassifyError={jobError}
                    reclassifyOutcome={jobOutcome}
                />
            )}
        </div>
    );
}

const panelMsgStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: '#5F6368', fontFamily: 'var(--font-family-primary)' };
const ctaStyle = { padding: '8px 20px', fontSize: 14, fontWeight: 600, color: '#FFFFFF', backgroundColor: '#1967D2', border: '1px solid #1967D2', borderRadius: 4, cursor: 'pointer', outline: 'none' };
const topBtnStyle = { fontSize: 12, padding: '4px 10px', borderRadius: 4, border: '1px solid #DADCE0', background: '#FFFFFF', color: '#5F6368', cursor: 'pointer' };
const exportMenuItemStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, color: '#202124', background: 'none', border: 'none', cursor: 'pointer' };
const legendStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: -4, fontSize: 12, color: '#3C4043' };
const legendItemStyle = { display: 'inline-flex', alignItems: 'center', gap: 5 };
const legendSwatchStyle = { display: 'inline-block', width: 12, height: 12, borderRadius: 2, border: '1px solid rgba(43, 107, 191, 0.35)' };
const legendLineStyle = { display: 'inline-block', width: 16, borderTop: '2px solid #2B6BBF' };
